import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

// FD-593: drag-to-reorder the Meeting Notes list rows. The DOM moves optimistically on drop
// (no re-render, like the kanban / favorites / status-page pattern); we then PATCH the full
// ordered list of note ids. On failure we revert to the last persisted order so the list
// can't drift from the DB. An AbortController supersedes an in-flight reorder so rapid drags
// can't land out of order. Mirrors favorites_reorder_controller / states_reorder_controller.
export default class extends Controller {
  static values = { url: String }

  connect() {
    this.order = this.currentIds() // last order known to be persisted
    this.sortable = Sortable.create(this.element, {
      handle: "[data-reorder-handle]",
      animation: 150,
      ghostClass: "sortable-ghost",
      onEnd: () => this.persist()
    })
  }

  disconnect() {
    this.sortable?.destroy()
    this.pending?.abort()
  }

  currentIds() {
    return Array.from(this.element.querySelectorAll("[data-note-id]")).map((el) => el.dataset.noteId)
  }

  async persist() {
    const ids = this.currentIds()
    this.pending?.abort() // a newer drag supersedes the one still in flight
    this.pending = new AbortController()
    try {
      const response = await fetch(this.urlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name=csrf-token]").content,
          Accept: "application/json"
        },
        body: JSON.stringify({ ids }),
        signal: this.pending.signal
      })
      if (!response.ok) return this.revert()
      this.order = ids // committed — this is now the order to revert to
    } catch (error) {
      if (error.name !== "AbortError") this.revert() // aborted ⇒ a newer drag is taking over
    }
  }

  // Restore row order to the last persisted state (a failed PATCH leaves the DB unchanged).
  revert() {
    const rows = new Map(
      Array.from(this.element.querySelectorAll("[data-note-id]")).map((el) => [el.dataset.noteId, el])
    )
    this.order.forEach((id) => {
      const el = rows.get(id)
      if (el) this.element.appendChild(el)
    })
  }
}
